import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Users, UserCheck, UserX, KeyRound, Eye, EyeOff, UserPlus, Pencil } from 'lucide-react';
import { Input } from '../../components/ui/input';
import api from '../../services/api';
import { toast } from 'sonner';

const ROLE_COLORS = {
  admin:                     { bg: '#fef2f2', text: '#991b1b', border: '#fecaca' },
  owner:                     { bg: '#fdf4ff', text: '#7e22ce', border: '#e9d5ff' },
  salesman:                  { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
  crm:                       { bg: '#f0fdf4', text: '#166534', border: '#bbf7d0' },
  audit:                     { bg: '#fff7ed', text: '#9a3412', border: '#fed7aa' },
  production_supervisor:     { bg: '#fefce8', text: '#854d0e', border: '#fde68a' },
  stock_supervisor:          { bg: '#ecfeff', text: '#155e75', border: '#a5f3fc' },
  worker:                    { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' },
  broker:                    { bg: '#f0f9ff', text: '#0369a1', border: '#bae6fd' },
  cashier:                   { bg: '#fdf2f8', text: '#9d174d', border: '#fbcfe8' },
  qc:                        { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' },
  inventory_dept:            { bg: '#fafaf9', text: '#44403c', border: '#e7e5e4' },
  diamond_dept:              { bg: '#f0fdfa', text: '#0f766e', border: '#99f6e4' },
  stone_dept:                { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe' },
  metal_inventory_assistant: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0' },
  advisor:                   { bg: '#f1f5f9', text: '#334155', border: '#cbd5e1' },
  order_advisor:             { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3' },
};

const getRoleStyle = (role) => ROLE_COLORS[role] || { bg: '#f8fafc', text: '#475569', border: '#e2e8f0' };

// All roles that admins can create/assign. Kept in sync with backend usage.
const ASSIGNABLE_ROLES = [
  'admin',
  'owner',
  'salesman',
  'production_supervisor',
  'stock_supervisor',
  'worker',
  'qc',
  'diamond_dept',
  'stone_dept',
  'inventory_dept',
  'metal_inventory_assistant',
  'broker',
  'cashier',
  'crm',
  'order_advisor',
  'audit',
  'advisor',
];

// Country codes for the mobile-number picker (matches the People-page UX)
const COUNTRY_CODES = [
  { code: '+91',  label: '🇮🇳 +91' },
  { code: '+971', label: '🇦🇪 +971' },
  { code: '+1',   label: '🇺🇸 +1' },
  { code: '+44',  label: '🇬🇧 +44' },
  { code: '+65',  label: '🇸🇬 +65' },
  { code: '+61',  label: '🇦🇺 +61' },
  { code: '+966', label: '🇸🇦 +966' },
  { code: '+974', label: '🇶🇦 +974' },
  { code: '+973', label: '🇧🇭 +973' },
  { code: '+968', label: '🇴🇲 +968' },
];

const EMPTY_FORM = {
  username: '',
  email: '',
  password: '',
  role: 'admin',
  department: '',
  salesman_id: '',
  countryCode: '+91',
  mobileNumber: '',
};

// Split an incoming mobile_number like "+919876543210" into {countryCode, mobileNumber}.
// Falls back to +91 when the prefix is unrecognised.
function splitMobile(full) {
  if (!full) return { countryCode: '+91', mobileNumber: '' };
  const match = COUNTRY_CODES.find(c => full.startsWith(c.code));
  if (match) return { countryCode: match.code, mobileNumber: full.slice(match.code.length) };
  // Unknown prefix — dump entire number into mobileNumber field with +91 default
  return { countryCode: '+91', mobileNumber: full.replace(/^\+/, '') };
}

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterRole, setFilterRole] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [toggling, setToggling] = useState(null);

  // Reset-password modal
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [resetting, setResetting] = useState(false);

  // Create / Edit user modal (shared)
  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState('create'); // 'create' | 'edit'
  const [formTarget, setFormTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showFormPwd, setShowFormPwd] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const openCreateModal = () => {
    setFormMode('create');
    setFormTarget(null);
    setForm(EMPTY_FORM);
    setShowFormPwd(false);
    setFormOpen(true);
  };

  const openEditModal = (user) => {
    setFormMode('edit');
    setFormTarget(user);
    const { countryCode, mobileNumber } = splitMobile(user.mobile_number);
    setForm({
      username: user.username || '',
      email: user.email || '',
      password: '',
      role: user.role || 'admin',
      department: user.department || '',
      salesman_id: user.salesman_id || '',
      countryCode,
      mobileNumber,
    });
    setShowFormPwd(false);
    setFormOpen(true);
  };

  const handleSubmitForm = async () => {
    const f = form;
    if (!f.username.trim()) { toast.error('Username is required'); return; }
    if (!f.email.trim())    { toast.error('Email is required');    return; }
    if (formMode === 'create' && !f.password) { toast.error('Password is required'); return; }
    if (f.password && f.password.length < 6)  { toast.error('Password must be at least 6 characters'); return; }
    if (!f.role) { toast.error('Role is required'); return; }
    if (f.role === 'salesman' && !f.salesman_id.trim()) {
      toast.error('Salesman ID is required for salesmen'); return;
    }

    const mobile = f.mobileNumber.trim() ? `${f.countryCode}${f.mobileNumber.trim()}` : null;

    setSubmitting(true);
    try {
      if (formMode === 'create') {
        await api.post('/admin/users', {
          username:      f.username.trim(),
          email:         f.email.trim().toLowerCase(),
          password:      f.password,
          role:          f.role,
          department:    f.department.trim() || null,
          salesman_id:   f.salesman_id.trim() || null,
          mobile_number: mobile,
        });
        toast.success(`User "${f.username}" created as ${f.role}`);
      } else {
        // Edit — send only changed / provided fields
        const payload = {};
        if (f.username.trim() !== formTarget.username)                       payload.username      = f.username.trim();
        if (f.email.trim().toLowerCase() !== (formTarget.email || '').toLowerCase()) payload.email  = f.email.trim().toLowerCase();
        if (f.password)                                                       payload.password      = f.password;
        if ((f.salesman_id.trim() || '') !== (formTarget.salesman_id || ''))  payload.salesman_id   = f.salesman_id.trim();
        if ((mobile || '') !== (formTarget.mobile_number || ''))              payload.mobile_number = mobile;
        // Role change goes through the dedicated PUT /admin/users/:id/role endpoint
        if (f.role !== formTarget.role) {
          await api.put(`/admin/users/${formTarget.id}/role`, { role: f.role });
        }
        if (Object.keys(payload).length > 0) {
          await api.put(`/admin/users/${formTarget.id}`, payload);
        }
        if (Object.keys(payload).length === 0 && f.role === formTarget.role) {
          toast.info('No changes to save');
          setSubmitting(false);
          return;
        }
        toast.success(`User "${f.username}" updated`);
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      await fetchUsers();
    } catch (e) {
      toast.error(e.response?.data?.detail || `Failed to ${formMode === 'create' ? 'create' : 'update'} user`);
    } finally {
      setSubmitting(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/admin/users');
      setUsers(res.data);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); }, []);

  const roles = useMemo(() => ['All', ...new Set(users.map(u => u.role).filter(Boolean)).values()].sort((a, b) => a === 'All' ? -1 : a.localeCompare(b)), [users]);

  const filtered = useMemo(() => users.filter(u => {
    const q = search.toLowerCase();
    if (q && !u.username.toLowerCase().includes(q) && !u.email.toLowerCase().includes(q)) return false;
    if (filterRole !== 'All' && u.role !== filterRole) return false;
    if (filterStatus === 'Active' && !u.is_active) return false;
    if (filterStatus === 'Inactive' && u.is_active !== false) return false;
    return true;
  }), [users, search, filterRole, filterStatus]);

  const activeCount = users.filter(u => u.is_active !== false).length;
  const inactiveCount = users.filter(u => u.is_active === false).length;

  const handleToggle = async (user) => {
    setToggling(user.id);
    try {
      const res = await api.patch(`/admin/users/${user.id}/toggle-active`);
      setUsers(prev => prev.map(u => u.id === user.id ? { ...u, is_active: res.data.is_active } : u));
      toast.success(`${user.username} ${res.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setToggling(null);
    }
  };

  const openResetModal = (user) => {
    setResetTarget(user);
    setResetPwd('');
    setResetConfirm('');
    setShowResetPwd(false);
  };

  const handleResetPassword = async () => {
    if (!resetPwd) { toast.error('Please enter a new password'); return; }
    if (resetPwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (resetPwd !== resetConfirm) { toast.error('Passwords do not match'); return; }
    setResetting(true);
    try {
      await api.post(`/admin/users/${resetTarget.id}/reset-password`, { new_password: resetPwd });
      toast.success(`Password reset for ${resetTarget.username}`);
      setResetTarget(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#075E54] to-[#128C7E] text-white px-4 py-4 flex-shrink-0">
        <div className="flex items-center gap-3 mb-3">
          <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-white/20">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold leading-tight">User Management</h1>
            <p className="text-xs text-emerald-200">{activeCount} active · {inactiveCount} inactive · {users.length} total</p>
          </div>
          <button
            onClick={openCreateModal}
            className="flex items-center gap-1.5 bg-white/15 hover:bg-white/25 active:bg-white/30 text-white text-xs font-semibold px-3 py-2 rounded-full transition-colors flex-shrink-0"
            data-testid="open-create-user-modal"
          >
            <UserPlus className="w-4 h-4" />
            New
          </button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Total', count: users.length, icon: Users },
            { label: 'Active', count: activeCount, icon: UserCheck },
            { label: 'Inactive', count: inactiveCount, icon: UserX },
          ].map(({ label, count, icon: Icon }) => (
            <div key={label} className="bg-white/10 rounded-xl px-3 py-2 text-center">
              <Icon className="w-4 h-4 mx-auto mb-0.5 text-emerald-200" />
              <div className="text-xl font-bold">{count}</div>
              <div className="text-[10px] text-emerald-200">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 pt-3 space-y-2 flex-shrink-0">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 h-9" />
        </div>

        <div className="flex gap-1.5">
          {['All', 'Active', 'Inactive'].map(s => (
            <button key={s} onClick={() => setFilterStatus(s)}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${filterStatus === s ? 'bg-[#075E54] text-white' : 'bg-gray-100 text-gray-600'}`}
              data-testid={`filter-status-${s.toLowerCase()}`}
            >{s}</button>
          ))}
        </div>

        <div className="flex gap-1.5 overflow-x-auto no-scrollbar pb-0.5">
          {roles.map(r => (
            <button key={r} onClick={() => setFilterRole(r)}
              className={`flex-shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-all border ${filterRole === r ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-white text-gray-600 border-gray-200'}`}
              data-testid={`filter-role-${r.toLowerCase()}`}
            >{r}</button>
          ))}
        </div>
      </div>

      {/* User List */}
      <div className="flex-1 overflow-y-auto px-4 pt-3 pb-6 space-y-2">
        {loading ? (
          <div className="flex justify-center py-12"><div className="w-6 h-6 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-400">
            <Users className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No users found</p>
          </div>
        ) : filtered.map(user => {
          const style = getRoleStyle(user.role);
          const isActive = user.is_active !== false;
          const isToggling = toggling === user.id;

          return (
            <div key={user.id}
              className={`rounded-xl border p-3.5 flex items-center gap-3 transition-all ${isActive ? 'bg-white border-gray-100 shadow-sm' : 'bg-gray-50 border-gray-200 opacity-60'}`}
              data-testid={`user-card-${user.id}`}
            >
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm ${isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-400'}`}>
                {user.username.charAt(0).toUpperCase()}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm text-gray-800 truncate">{user.username}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
                    style={{ background: style.bg, color: style.text, border: `1px solid ${style.border}` }}>
                    {user.role}
                  </span>
                  {!isActive && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 border border-red-200 font-semibold">Inactive</span>
                  )}
                </div>
                <p className="text-xs text-gray-400 truncate mt-0.5">{user.email}</p>
                {user.mobile_number && (
                  <p className="text-[11px] text-gray-500 truncate mt-0.5 font-mono">{user.mobile_number}</p>
                )}
              </div>

              <div className="flex items-center gap-1 shrink-0">
                {/* Edit */}
                <button
                  onClick={() => openEditModal(user)}
                  title="Edit user"
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-emerald-600 rounded-full hover:bg-emerald-50 transition-colors"
                  data-testid={`edit-user-${user.id}`}
                >
                  <Pencil className="w-4 h-4" />
                </button>

                {/* Reset Password */}
                <button
                  onClick={() => openResetModal(user)}
                  title="Reset password"
                  className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-emerald-600 rounded-full hover:bg-emerald-50 transition-colors"
                  data-testid={`reset-pwd-${user.id}`}
                >
                  <KeyRound className="w-4 h-4" />
                </button>

                {/* Toggle Active */}
                <button
                  onClick={() => handleToggle(user)}
                  disabled={isToggling}
                  data-testid={`toggle-active-${user.id}`}
                  title={isActive ? 'Deactivate user' : 'Activate user'}
                  className={`flex-shrink-0 w-12 h-6 rounded-full relative transition-all duration-300 ${isActive ? 'bg-emerald-500' : 'bg-gray-300'} ${isToggling ? 'opacity-50' : ''}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all duration-300 ${isActive ? 'left-6' : 'left-0.5'}`} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reset Password Modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => setResetTarget(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-4 max-w-lg mx-auto" onClick={e => e.stopPropagation()}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                <KeyRound className="w-5 h-5 text-emerald-700" />
              </div>
              <div>
                <h2 className="font-bold text-gray-900 text-base">Reset Password</h2>
                <p className="text-sm text-gray-400">{resetTarget.username} · {resetTarget.email}</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="relative">
                <input
                  type={showResetPwd ? 'text' : 'password'}
                  placeholder="New password (min 6 characters)"
                  value={resetPwd}
                  onChange={e => setResetPwd(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="reset-pwd-input"
                />
                <button
                  onClick={() => setShowResetPwd(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  type="button"
                >
                  {showResetPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <input
                type={showResetPwd ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={resetConfirm}
                onChange={e => setResetConfirm(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="reset-pwd-confirm"
              />
            </div>
            <button
              onClick={handleResetPassword}
              disabled={resetting}
              className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60"
              data-testid="reset-pwd-submit"
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit User Modal */}
      {formOpen && (
        <div className="fixed inset-0 z-50 flex items-end" onClick={() => !submitting && setFormOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="relative w-full bg-white rounded-t-3xl px-5 pt-5 pb-10 space-y-4 max-w-lg mx-auto max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
            data-testid={formMode === 'create' ? 'create-user-modal' : 'edit-user-modal'}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-1" />
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                {formMode === 'create' ? <UserPlus className="w-5 h-5 text-emerald-700" /> : <Pencil className="w-5 h-5 text-emerald-700" />}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="font-bold text-gray-900 text-base">{formMode === 'create' ? 'Create New User' : 'Edit User'}</h2>
                <p className="text-sm text-gray-400 truncate">
                  {formMode === 'create' ? 'Assign a role including admin or owner' : `${formTarget?.username} · ${formTarget?.email}`}
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <input
                type="text"
                placeholder="Username *"
                value={form.username}
                onChange={e => setForm(f => ({ ...f, username: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="form-user-username"
                autoComplete="off"
              />

              <input
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="form-user-email"
                autoComplete="off"
              />

              <div className="relative">
                <input
                  type={showFormPwd ? 'text' : 'password'}
                  placeholder={formMode === 'create' ? 'Password (min 6 characters) *' : 'New password (leave blank to keep current)'}
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 pr-11 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="form-user-password"
                  autoComplete="new-password"
                />
                <button
                  onClick={() => setShowFormPwd(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                  type="button"
                >
                  {showFormPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <div>
                <label className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide ml-1 mb-1 block">Role *</label>
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                  data-testid="form-user-role"
                >
                  {ASSIGNABLE_ROLES.map(r => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
              </div>

              <input
                type="text"
                placeholder={form.role === 'worker' ? 'Worker Code (optional)' : 'Salesman ID' + (form.role === 'salesman' ? ' *' : ' (optional)')}
                value={form.salesman_id}
                onChange={e => setForm(f => ({ ...f, salesman_id: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="form-user-salesman-id"
              />

              <div>
                <label className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide ml-1 mb-1 block">Mobile Number (optional)</label>
                <div className="flex gap-2">
                  <select
                    value={form.countryCode}
                    onChange={e => setForm(f => ({ ...f, countryCode: e.target.value }))}
                    className="border border-gray-200 rounded-xl px-3 py-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    data-testid="form-user-country-code"
                  >
                    {COUNTRY_CODES.map(c => (
                      <option key={c.code} value={c.code}>{c.label}</option>
                    ))}
                  </select>
                  <input
                    type="tel"
                    inputMode="numeric"
                    placeholder="9876543210"
                    value={form.mobileNumber}
                    onChange={e => setForm(f => ({ ...f, mobileNumber: e.target.value.replace(/\D/g, '') }))}
                    className="flex-1 min-w-0 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    data-testid="form-user-mobile"
                  />
                </div>
              </div>

              <input
                type="text"
                placeholder="Department (optional)"
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400"
                data-testid="form-user-department"
              />
            </div>

            <button
              onClick={handleSubmitForm}
              disabled={submitting}
              className="w-full bg-emerald-600 text-white font-bold py-3.5 rounded-xl disabled:opacity-60 mt-2"
              data-testid={formMode === 'create' ? 'create-user-submit' : 'edit-user-submit'}
            >
              {submitting
                ? (formMode === 'create' ? 'Creating…' : 'Saving…')
                : (formMode === 'create' ? 'Create User'   : 'Save Changes')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
